import { useState, useEffect, lazy, Suspense } from "react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { MortgageForm } from "@/features/calculator";
import { ErrorBoundary } from "@/components/ui";
import { MortgageFormData } from "@/types";
import { logger } from "@/lib";
import { getUserEmail, getUserId, getUserRoles, getUserPermissions, extractRolesAndPermissionsFromJWT } from "@/lib";
import { Toaster } from "sonner";
// Shared components
import { Container } from "@/shared/components";
import { APP_CONFIG, ROUTES, FEATURE_FLAGS } from "@/constants";
import {useAuth} from "@workos-inc/authkit-react";
import { Authenticated, Unauthenticated, useConvexAuth, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

// Lazy load components that are conditionally rendered
const SavedResults = lazy(() => import("@/features/results").then(module => ({ default: module.SavedResults })));
const AdminConfig = lazy(() => import("@/features/admin").then(module => ({ default: module.AdminConfig })));
const PrivacyPolicy = lazy(() => import("@/features/auth").then(module => ({ default: module.PrivacyPolicy })));
const CalculationViewer = lazy(() => import("@/features/results").then(module => ({ default: module.CalculationViewer })));

// Global error handlers
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection', new Error(event.reason), {
    type: 'unhandledrejection',
    reason: event.reason,
  });
});

window.addEventListener('error', (event) => {
  logger.error('Uncaught error', event.error, {
    type: 'uncaught',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

type TabType = keyof typeof ROUTES;
// This is a test comment to test our GitHub integration with Jira! 
// This is a second test comment to test our GitHub integration with Jira!  
export default function App() {
  const { user, signIn, signOut, getAccessToken } = useAuth();
  const [jwtPayload, setJwtPayload] = useState<any>(null);
  
  // Extract roles and permissions from JWT token
  const { roles: userRoles, permissions: userPermissions } = extractRolesAndPermissionsFromJWT(jwtPayload);
  
  const canViewResults = userPermissions.includes("loan-calc:saved-results:read");
  const canAccessAdminTab = userPermissions.includes("loan-calc:variables:read");
  const canWriteVars = userPermissions.includes("loan-calc:variables:write");
  const userEmail = getUserEmail(user) || "";
  
  // Extract JWT payload when user changes
  useEffect(() => {
    if (user) {
      getAccessToken().then(token => {
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setJwtPayload(payload);
            console.log("JWT Payload:", payload);
          } catch (e) {
            console.log("Could not decode JWT payload:", e);
            setJwtPayload(null);
          }
        }
      }).catch(err => {
        console.log("Error getting access token:", err);
        setJwtPayload(null);
      });
    } else {
      setJwtPayload(null);
    }
  }, [user, getAccessToken]);
  
  // Debug: Log user object and roles (remove in production)
  useEffect(() => {
    if (user) {
      console.log("User object:", user);
      console.log("User roles:", userRoles);
      console.log("User permissions:", userPermissions);
      console.log("Can view results:", canViewResults);
      console.log("Can access admin tab:", canAccessAdminTab);
      console.log("Can write variables:", canWriteVars);
    }
  }, [user, userRoles, userPermissions, canViewResults, canAccessAdminTab, canWriteVars]);
  // Check Convex auth state
  const { isAuthenticated: convexIsAuthenticated, isLoading: convexAuthLoading } = useConvexAuth();
  const [activeTab, setActiveTab] = useState<TabType>("CALCULATOR");
  const [loadedFormData, setLoadedFormData] = useState<MortgageFormData | undefined>(undefined);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [calculationViewerMode, setCalculationViewerMode] = useState<{
    isViewer: boolean;
    resultId?: string;
  }>({ isViewer: false });
  

  // Sync logger user context with auth changes
  useEffect(() => {
    const userId = getUserId(user) || "anonymous";
    const email = getUserEmail(user) || undefined;
    if (user) {
      // Async setUser to avoid blocking render
      void logger.setUser(userId, email, canAccessAdminTab).then(() => {
        logger.userSignIn();
      });
    } else {
      logger.userSignOut();
      // Async clearUser to avoid blocking render
      void logger.clearUser();
    }
  }, [user, canAccessAdminTab]);


  // Check URL for calculation viewer
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    // Check for calculation viewer mode
    const isViewer = urlParams.get('viewer') === 'true' || hash === '#calculation-viewer';
    const resultId = urlParams.get('resultId');
    
    if (isViewer && resultId) {
      setCalculationViewerMode({ isViewer: true, resultId });
    }
  }, []);

  // Page view logging
  useEffect(() => {
    logger.pageView(activeTab.toLowerCase());
  }, [activeTab]);

  const handleFormDataLoaded = () => {
    setLoadedFormData(undefined);
  };

  const handleTabChange = (newTab: TabType) => {
    logger.tabChanged(activeTab.toLowerCase(), newTab.toLowerCase());
    // Gate Results tab to users who can view results
    if (newTab === "RESULTS" && !canViewResults) {
      setActiveTab("CALCULATOR");
      return;
    }
    // Gate Admin tab to users who can access admin panel
    if (newTab === "ADMIN" && !canAccessAdminTab) {
      setActiveTab("CALCULATOR");
      return;
    }
    setActiveTab(newTab);
    setIsMobileMenuOpen(false);
  };


  const AppContent = () => {
    // Show calculation viewer if in viewer mode
    if (calculationViewerMode.isViewer) {
      return (
        <Suspense fallback={
          <div className="flex justify-center items-center min-h-screen">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <CalculationViewer resultId={calculationViewerMode.resultId} />
        </Suspense>
      );
    }


    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gray-50 dark:bg-navy-dark flex flex-col">
          <Toaster 
            position="top-right" 
            richColors 
            theme="system"
            toastOptions={{
              style: {
                background: 'var(--color-light)',
                color: 'var(--color-dark)',
                border: '1px solid #e5e7eb',
              },
              className: 'dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600',
            }}
          />
          
          {/* Header */}
          <AppHeader
            activeTab={activeTab}
            isMobileMenuOpen={isMobileMenuOpen}
            onTabChange={handleTabChange}
            onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            canViewResults={canViewResults}
            canAccessAdmin={canAccessAdminTab}
            onSignIn={signIn}
            signOut={signOut}
            isAuthenticated={!!user}
            userEmail={userEmail}
          />

          {/* Main Content */}
          <main className="flex-1 py-6">
            {activeTab === "CALCULATOR" ? (
              <MortgageForm 
                loadedFormData={loadedFormData}
                onFormDataLoaded={handleFormDataLoaded}
              />
            ) : activeTab === "RESULTS" ? (
              canViewResults ? (
                <Suspense fallback={
                  <div className="flex justify-center items-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                }>
                  <SavedResults />
                </Suspense>
              ) : null
            ) : activeTab === "ADMIN" ? (
              canAccessAdminTab ? (
                <Suspense fallback={
                  <div className="flex justify-center items-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                }>
                  <AdminConfig canWriteVariables={canWriteVars} />
                </Suspense>
              ) : null
            ) : activeTab === "PRIVACY" ? (
              <Suspense fallback={
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              }>
                <PrivacyPolicy />
              </Suspense>
            ) : null}
          </main>

          {/* Footer */}
          <AppFooter activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
      </ErrorBoundary>
    );
  };

  return <AppContent />;
}

// Extracted Header Component
interface AppHeaderProps {
  activeTab: TabType;
  isMobileMenuOpen: boolean;
  onTabChange: (tab: TabType) => void;
  onToggleMobileMenu: () => void;
  canViewResults: boolean;
  canAccessAdmin: boolean;
  isAuthenticated: boolean;
  onSignIn: () => void;
  signOut: (options?: { returnTo?: string }) => void;
  userEmail?: string;
}

function AppHeader({
  activeTab,
  isMobileMenuOpen,
  onTabChange,
  onToggleMobileMenu,
  canViewResults,
  canAccessAdmin,
  isAuthenticated,
  onSignIn,
  signOut,
  userEmail,
}: AppHeaderProps) {
  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-navy shadow-sm border-b border-gray-200 dark:border-gray-600">
      <div className="w-full px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Title - Left aligned with split logo */}
          <div className="flex-shrink-0 flex items-center">
            <SplitLogo onLogoClick={() => onTabChange("CALCULATOR")} />
          </div>

          {/* Desktop Navigation - Center */}
          <nav className="hidden md:flex space-x-4">
            <Authenticated>
              <TabButton
                active={activeTab === "CALCULATOR"}
                onClick={() => onTabChange("CALCULATOR")}
              >
                Calculator
              </TabButton>
              {canViewResults && (
                <TabButton
                  active={activeTab === "RESULTS"}
                  onClick={() => onTabChange("RESULTS")}
                >
                  Saved Results
                </TabButton>
              )}
              {canAccessAdmin && (
                <TabButton
                  active={activeTab === "ADMIN"}
                  onClick={() => onTabChange("ADMIN")}
                >
                  Admin
                </TabButton>
              )}
            </Authenticated>
          </nav>

          {/* Desktop Controls - Right aligned */}
          <div className="hidden md:flex items-center space-x-4">
            <DarkModeToggle />
            {FEATURE_FLAGS.ENABLE_SIGN_IN && (
              <Unauthenticated>
                <button
                  onClick={onSignIn}
                  className="px-3 py-2 rounded-md text-sm font-subheader text-white bg-cyan hover:bg-cyan-dark transition-colors dark:bg-cyan-light dark:hover:bg-cyan"
                >
                  Sign in
                </button>
              </Unauthenticated>
            )}
            <Authenticated>
              <div className="flex items-center space-x-2">
                {canAccessAdmin && (
                  <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Admin</span>
                )}
                {userEmail && (
                  <span className="hidden sm:inline text-xs text-gray-600 dark:text-gray-300">{userEmail}</span>
                )}
                <button
                  onClick={() => signOut({ returnTo: `${window.location.origin}/` })}
                  className="px-3 py-2 rounded-md text-sm font-subheader text-gray-700 dark:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-navy-light dark:hover:bg-navy transition-colors"
                >
                  Sign out
                </button>
              </div>
            </Authenticated>
          </div>

          {/* Mobile Controls - Right aligned */}
          <div className="flex md:hidden items-center space-x-2">
            <DarkModeToggle />
            <MobileMenuButton
              isOpen={isMobileMenuOpen}
              onToggle={onToggleMobileMenu}
            />
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <MobileMenu
            activeTab={activeTab}
            onTabChange={onTabChange}
            canViewResults={canViewResults}
            canAccessAdmin={canAccessAdmin}
            isAuthenticated={isAuthenticated}
            onSignIn={onSignIn}
            signOut={signOut}
            userEmail={userEmail}
          />
        )}
      </div>
    </header>
  );
}

// Footer Component
interface AppFooterProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

function AppFooter({ activeTab, onTabChange }: AppFooterProps) {
  // Add bottom margin on calculator page to ensure privacy policy link is visible above any sticky elements
  const footerClasses = activeTab === "CALCULATOR" 
    ? "bg-white dark:bg-navy border-t border-gray-200 dark:border-gray-600 py-4 mb-20"
    : "bg-white dark:bg-navy border-t border-gray-200 dark:border-gray-600 py-4";

  return (
    <footer className={footerClasses}>
      <Container>
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            © 2025 Market Harborough Building Society
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onTabChange("PRIVACY")}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              Cookie Policy
            </button>
            <a
              href="https://mhbs.co.uk/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}

// Supporting Components
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-md text-sm font-subheader transition-colors ${
        active
          ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300"
          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-navy-light"
      }`}
    >
      {children}
    </button>
  );
}


function MobileMenuButton({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
    >
      <span className="sr-only">Open main menu</span>
      <svg
        className={`${isOpen ? 'hidden' : 'block'} h-6 w-6`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      <svg
        className={`${isOpen ? 'block' : 'hidden'} h-6 w-6`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

interface MobileMenuProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  canViewResults: boolean;
  canAccessAdmin: boolean;
  isAuthenticated: boolean;
  onSignIn: () => void;
  signOut: (options?: { returnTo?: string }) => void;
  userEmail?: string;
}

function MobileMenu({ activeTab, onTabChange, canViewResults, canAccessAdmin, isAuthenticated, onSignIn, signOut, userEmail }: MobileMenuProps) {
  return (
    <div className="md:hidden">
      <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200 dark:border-gray-600">
        <Authenticated>
          <MobileTabButton
            active={activeTab === "CALCULATOR"}
            onClick={() => onTabChange("CALCULATOR")}
          >
            Calculator
          </MobileTabButton>
          {canViewResults && (
            <MobileTabButton
              active={activeTab === "RESULTS"}
              onClick={() => onTabChange("RESULTS")}
            >
              Saved Results
            </MobileTabButton>
          )}
          {canAccessAdmin && (
            <MobileTabButton
              active={activeTab === "ADMIN"}
              onClick={() => onTabChange("ADMIN")}
            >
              Admin
            </MobileTabButton>
          )}
        </Authenticated>
        <div className="pt-2 flex items-center space-x-2">
          {FEATURE_FLAGS.ENABLE_SIGN_IN && (
            <Unauthenticated>
              <button
                onClick={onSignIn}
                className="w-full px-3 py-2 rounded-md text-sm font-subheader text-white bg-cyan hover:bg-cyan-dark transition-colors dark:bg-cyan-light dark:hover:bg-cyan"
              >
                Sign in
              </button>
            </Unauthenticated>
          )}
          <Authenticated>
            {userEmail && (
              <span className="flex-1 text-xs text-gray-600 dark:text-gray-300 truncate pr-2">{userEmail}</span>
            )}
            <button
              onClick={() => signOut({ returnTo: `${window.location.origin}/` })}
              className="w-full px-3 py-2 rounded-md text-sm font-subheader text-gray-700 dark:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-navy-light dark:hover:bg-navy transition-colors"
            >
              Sign out
            </button>
          </Authenticated>
        </div>
      </div>
    </div>
  );
}

function MobileTabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left px-3 py-2 rounded-md text-base font-subheader transition-colors ${
        active
          ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300"
          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-navy-light"
      }`}
    >
      {children}
    </button>
  );
}

// Split logo component with left part maintaining colors and right part adapting to dark mode
interface SplitLogoProps {
  onLogoClick?: () => void;
}

function SplitLogo({ onLogoClick }: SplitLogoProps) {
  // Set explicit dimensions to prevent CLS (Cumulative Layout Shift)
  // Height is 64px (h-16), width estimated based on typical logo proportions
  // These dimensions reserve space before images load, preventing layout shifts
  return (
    <div 
      className="flex items-center h-16 cursor-pointer" 
      style={{ minWidth: '240px' }}
      onClick={onLogoClick}
      role={onLogoClick ? "button" : undefined}
      tabIndex={onLogoClick ? 0 : undefined}
      onKeyDown={onLogoClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLogoClick();
        }
      } : undefined}
    >
      {/* Left part - maintains original colors */}
      <img 
        src="/main-hori-full-blue-left.png" 
        alt={APP_CONFIG.NAME}
        className="h-full w-auto"
        width="120"
        height="64"
        loading="eager"
        style={{ display: 'block', objectFit: 'contain' }}
      />
      {/* Right part - white text in dark mode */}
      <img 
        src="/main-hori-full-blue-right.png" 
        alt=""
        className="h-full w-auto dark:brightness-0 dark:invert"
        width="120"
        height="64"
        loading="eager"
        style={{ display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
}
